import { BarChart2, Building, CheckCircle, Eye, Filter, MapPin, RefreshCw, Search, Trash2, Users } from "lucide-react";
import { useState, useEffect } from "react";
import {
    getEvents,
    getMonthlyEventStats,
    triggerEnrichment,
    deleteEvent,
    calculateEventScore,
    updateEvent,
    Event,
} from "../../interfaces/events";
import BONavigation from "../../components/admin/BONavigation";
import EventDetailModal from "./EventDetailModal";
import { useNavigate } from "react-router-dom";

export default function EventAdminDashboard() {
    const [events, setEvents] = useState<Event[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [showFilters, setShowFilters] = useState(false);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [selectedMonth, setSelectedMonth] = useState<number | null>(null);
    const [monthlyStats, setMonthlyStats] = useState<Array<{
        monthIndex: number;
        startDate: string;
        endDate: string;
        total: number;
        handled: number;
        unhandled: number;
        handledPercentage: number;
        linkedinApproved: number;
        linkedinApprovedPercentage: number;
        gmapsApproved: number;
        gmapsApprovedPercentage: number;
        emailApproved: number;
        emailApprovedPercentage: number;
    }>>([]);
    const [editingEventId, setEditingEventId] = useState<string | null>(null);
    const [editingNotes, setEditingNotes] = useState<string>("");
    const [pendingEnrichmentType, setPendingEnrichmentType] = useState("");
    const [pendingEnrichmentStatus, setPendingEnrichmentStatus] = useState("");
    const nav = useNavigate();

    const [filters, setFilters] = useState<{
        signalCreated?: string;
        minScore?: string;
        maxScore?: string;
        enrichmentStatus?: string;
        startDate?: string;
        endDate?: string;
        eventType?: string;
        hasNotes?: string;
        city?: string;
        sortBy?: string;
        sortOrder?: string;
        enrichmentFilters?: Array<{ type: string; status?: string }>;
    }>({});

    // Helper function to get color based on handled percentage
    const getPercentageColor = (percentage: number): string => {
        if (percentage >= 80) return "bg-green-500 text-white";
        if (percentage >= 50) return "bg-orange-500 text-white";
        return "bg-red-500 text-white";
    };

    const getPercentageColorUnselected = (percentage: number): string => {
        if (percentage >= 80) return "bg-green-100 text-green-700";
        if (percentage >= 50) return "bg-orange-100 text-orange-700";
        return "bg-red-100 text-red-700";
    };

    // Fetch monthly stats on component mount and when eventType filter changes
    useEffect(() => {
        const fetchMonthlyStats = async () => {
            try {
                const response = await getMonthlyEventStats(12, filters.eventType);
                setMonthlyStats(response.data || []);
            } catch (err) {
                console.error("Failed to fetch monthly stats:", err);
            }
        };
        fetchMonthlyStats();
    }, [filters.eventType]);

    // Handle month selection
    const handleMonthSelect = (monthIndex: number | null) => {
        setSelectedMonth(monthIndex);
        setPage(1);

        if (monthIndex === null) {
            setFilters(prev => {
                const newFilters = { ...prev };
                delete newFilters.startDate;
                delete newFilters.endDate;
                return newFilters;
            });
        } else {
            const month = monthlyStats.find(m => m.monthIndex === monthIndex);
            if (month) {
                setFilters(prev => ({
                    ...prev,
                    startDate: month.startDate,
                    endDate: month.endDate
                }));
            }
        }
    };

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters, page]);

    const fetchData = async () => {
        try {
            setLoading(true);
            const { enrichmentFilters: enrichmentFiltersArr, ...restFilters } = filters;
            const params: any = { ...restFilters, page, limit: 20 };
            if (enrichmentFiltersArr?.length) {
                params.enrichmentFilters = JSON.stringify(enrichmentFiltersArr);
            }

            // Only include search if it's not empty
            if (searchTerm && searchTerm.trim()) {
                params.search = searchTerm.trim();
            }

            const response = await getEvents(params);
            
            if (page === 1) {
                setEvents(response.data || []);
            } else {
                setEvents(prev => [...prev, ...(response.data || [])]);
            }

            setHasMore(response.data?.length === 20);
        } catch (err) {
            console.error("Failed to fetch events:", err);
            setError("Failed to load events");
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = () => {
        setPage(1);
        fetchData();
    };

    const handleFilterChange = (key: string, value: any) => {
        setPage(1);
        setFilters(prev => ({ ...prev, [key]: value || undefined }));
    };

    const addEnrichmentFilter = () => {
        if (!pendingEnrichmentType) return;
        const entry: { type: string; status?: string } = { type: pendingEnrichmentType };
        if (pendingEnrichmentStatus) entry.status = pendingEnrichmentStatus;
        setPage(1);
        setFilters(prev => ({ ...prev, enrichmentFilters: [...(prev.enrichmentFilters || []), entry] }));
        setPendingEnrichmentType("");
        setPendingEnrichmentStatus("");
    };

    const removeEnrichmentFilter = (index: number) => {
        setPage(1);
        setFilters(prev => {
            const next = (prev.enrichmentFilters || []).filter((_, i) => i !== index);
            return { ...prev, enrichmentFilters: next.length > 0 ? next : undefined };
        });
    };

    const handleDelete = async (eventId: string) => {
        if (!window.confirm("Are you sure you want to delete this event?")) {
            return;
        }

        try {
            await deleteEvent(eventId);
            setEvents(prev => prev.filter(e => e._id !== eventId));
        } catch (err) {
            console.error("Failed to delete event:", err);
            alert("Failed to delete event");
        }
    };

    const handleViewDetails = (event: Event) => {
        setSelectedEvent(event);
        setShowDetailModal(true);
    };

    const handleTriggerEnrichment = async (eventId: string, type: string) => {
        try {
            await triggerEnrichment(eventId, type);
            fetchData();
            if (selectedEvent?._id === eventId) {
                const updated = await getEvents({ page: 1, limit: 1, search: eventId });
                setSelectedEvent(updated.data[0]);
            }
        } catch (err) {
            console.error("Failed to trigger enrichment:", err);
            alert("Failed to trigger enrichment");
        }
    };

    const handleCreateCompanyAndSignals = async (eventId: string) => {

        // Find the event data
        const event = events.find(e => e._id === eventId);
        if (!event) {
            console.error("❌ Event not found:", eventId);
            alert("Event not found");
            return;
        }

        // Prepare pre-filled data for the form
        const prefilledData = {
            // Company data
            companyName: event.companyName,
            companyDisplayName: (() => {
                const restaurantNameEnrichment = event.enrichments?.find((e: any) => e.type === 'restaurant_name');
                return (restaurantNameEnrichment?.status === 'done' ? restaurantNameEnrichment?.data?.restaurant_name || "" : "");
            })(),
            companyWebsite: (() => {
                // Prefer Google Place website if available
                const googlePlaceEnrichment = event.enrichments?.find((e: any) => e.type === 'google_place');
                const googlePlaceWebsite = googlePlaceEnrichment?.status === 'done' ? googlePlaceEnrichment?.data?.website_uri || "" : "";

                if (googlePlaceWebsite) return googlePlaceWebsite;

                // Fallback to website enrichment
                const websiteEnrichment = event.enrichments?.find((e: any) => e.type === 'website');
                return (websiteEnrichment?.status === 'done' ? websiteEnrichment?.data?.url || "" : "") || "";
            })(),
            companyIndustry: "",
            companyLocation: (() => {
                const addressEnrichment = event.enrichments?.find((e: any) => e.type === 'address');
                return (addressEnrichment?.status === 'done' ? addressEnrichment?.data?.address || "" : "");
            })(),
            companyDescription: `Company detected from ${event.source} event`,
            companyStatus: "prospect",
            companyPriority: "medium",
            companyTags: [`detected-from-${event.source}`],

            // Extract Google Place data from enrichments
            companyPhone: (() => {
                const googlePlaceEnrichment = event.enrichments?.find((e: any) => e.type === 'google_place');
                return (googlePlaceEnrichment?.status === 'done' ? googlePlaceEnrichment?.data?.national_phone_number || "" : "");
            })(),
            companyGoogleRating: (() => {
                const googlePlaceEnrichment = event.enrichments?.find((e: any) => e.type === 'google_place');
                return (googlePlaceEnrichment?.status === 'done' ? googlePlaceEnrichment?.data?.rating : undefined);
            })(),
            companyGoogleReviewCount: (() => {
                const googlePlaceEnrichment = event.enrichments?.find((e: any) => e.type === 'google_place');
                return (googlePlaceEnrichment?.status === 'done' ? googlePlaceEnrichment?.data?.user_ratings_total : undefined);
            })(),
            companyGoogleMapsUrl: (() => {
                const googlePlaceEnrichment = event.enrichments?.find((e: any) => e.type === 'google_place');
                return (googlePlaceEnrichment?.status === 'done' ? googlePlaceEnrichment?.data?.google_maps_url || "" : "");
            })(),
            companySiren: (() => {
                const sirenEnrichment = event.enrichments?.find((e: any) => e.type === 'siren');
                return (sirenEnrichment?.status === 'done' ? sirenEnrichment?.data?.siren || "" : "");
            })(),

            // Extract image URL from enrichments
            signalImageUrl: (() => {
                const imageEnrichment = event.enrichments?.find((e: any) => e.type === 'image');
                return imageEnrichment?.status === 'done' ? imageEnrichment?.data?.url || "" : "";
            })(),

            // Signal data
            signalTitle: `Signal ${event.companyName}`,
            signalDescription: `N/A`,
            signalEventType: "new-restaurant",
            signalSource: 'DuckFlow Radar',
            signalSourceUrl: event.sourceUrl,
            signalImportance: "medium",
            signalTags: [] as any,

            // Contact data from people with enrichment data
            contacts: event.people?.map((person: any, index: number) => {
                // Extract email from enrichments
                const emailEnrichment = person.enrichments?.find((e: any) =>
                    e.type === 'email_personal' || e.type === 'email_company'
                );
                const email = emailEnrichment?.status === 'done' ? emailEnrichment?.data?.email || "" : "";

                // Extract LinkedIn from enrichments
                const linkedinEnrichment = person.enrichments?.find((e: any) => e.type === 'linkedin');
                const linkedinUrl = linkedinEnrichment?.status === 'done' ? linkedinEnrichment?.data?.url || "" : "";

                return {
                    name: person.name || "",
                    firstName: person.name?.split(' ')[0] || "",
                    lastName: person.name?.split(' ').slice(1).join(' ') || "",
                    title: "",
                    email: email,
                    linkedinUrl: linkedinUrl,
                    phone: "",
                    isPrimary: person.role === "primary" || index === 0
                };
            }) || [],

            // Event reference
            sourceEventId: eventId,
            
            // Detected date for publishedAt
            detectedDate: event.detectedAt
        };

        localStorage.setItem('prefilled-company-signal-data', JSON.stringify(prefilledData));

        nav('/bo/create-company-signal');
    };

    const handleRefreshEvent = async () => {
        try {
            await fetchData();
            // Refresh the selected event in the modal
            if (selectedEvent) {
                const response = await getEvents({ page: 1, limit: 100 });
                const updated = response.data.find((e: Event) => e._id === selectedEvent._id);
                if (updated) {
                    setSelectedEvent(updated);
                }
            }
        } catch (err) {
            console.error("Failed to refresh event:", err);
        }
    };

    const loadMore = () => {
        setPage(prev => prev + 1);
    };

    const formatDate = (dateString?: string) => {
        if (!dateString) return "N/A";
        return new Date(dateString).toLocaleString("en-US", { 
            month: "short", 
            day: "numeric", 
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit"
        });
    };

    const _getStatusColor = (status: string) => {
        switch (status) {
            case "done": return "bg-green-50 text-green-800 border-green-200";
            case "pending": return "bg-yellow-50 text-yellow-800 border-yellow-200";
            case "error": return "bg-red-50 text-red-800 border-red-200";
            default: return "bg-gray-50 text-gray-800 border-gray-200";
        }
    };

    const _getStageColor = (stage: string) => {
        const colors: Record<string, string> = {
            raw: "bg-gray-50 text-gray-800 border-gray-200",
            deduplicated: "bg-gray-100 text-gray-700 border-gray-200",
            enriched_partial: "bg-orange-50 text-orange-800 border-orange-200",
            enriched_full: "bg-green-50 text-green-800 border-green-200",
            scored: "bg-purple-50 text-purple-800 border-purple-200",
            completed: "bg-teal-50 text-teal-800 border-teal-200",
            error: "bg-red-50 text-red-800 border-red-200"
        };
        return colors[stage] || "bg-gray-50 text-gray-800 border-gray-200";
    };


    const _getAddressMatchBadge = (quality: string) => {
        switch (quality) {
            case "really_close": return "bg-green-50 text-green-800 border-green-200";
            case "close": return "bg-orange-50 text-orange-800 border-orange-200";
            case "not_matching": return "bg-red-50 text-red-800 border-red-200";
            default: return "bg-gray-50 text-gray-800 border-gray-200";
        }
    };

    const handleCalculateScore = async (eventId: string) => {
        try {
            await calculateEventScore(eventId);
            await fetchData();
            if (selectedEvent?._id === eventId) {
                const response = await getEvents({ page: 1, limit: 100 });
                const updated = response.data.find((e: Event) => e._id === eventId);
                if (updated) {
                    setSelectedEvent(updated);
                }
            }
        } catch (err) {
            console.error("Failed to calculate score:", err);
            alert("Failed to calculate score");
        }
    };

    const handleStartEditing = (event: Event) => {
        setEditingEventId(event._id);
        setEditingNotes(event.notes || "");
    };

    const handleCancelEditing = () => {
        setEditingEventId(null);
        setEditingNotes("");
    };

    const handleSaveNotes = async (eventId: string) => {
        try {
            await updateEvent(eventId, {
                notes: editingNotes
            });
            
            // Update local state
            setEvents(prev => prev.map(e => 
                e._id === eventId 
                    ? { ...e, notes: editingNotes }
                    : e
            ));
            
            handleCancelEditing();
        } catch (err) {
            console.error("Failed to update event:", err);
            alert("Failed to update event notes");
        }
    };

    return (
        <div className="min-h-screen bg-[#F5F0EB]">
            <div className="mx-auto max-w-7xl px-4">
                {/* Tabs */}
                <BONavigation />

                {/* Month Filter */}
                <div className="mb-6">
                    <div className="bg-white border border-gray-200 rounded-xs p-4">
                        <div className="flex items-center justify-between mb-3">
                            <h3 className="text-xs font-semibold text-gray-700 uppercase tracking-wider">
                                Filter by Month
                            </h3>
                            {selectedMonth !== null && (
                                <button
                                    onClick={() => handleMonthSelect(null)}
                                    className="text-xs text-gray-900 hover:underline font-medium"
                                >
                                    Clear filter
                                </button>
                            )}
                        </div>
                        <div className="flex items-start gap-2 overflow-x-auto pb-2 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
                            {monthlyStats.map((month) => {
                                const isSelected = selectedMonth === month.monthIndex;
                                return (
                                    <button
                                        key={month.monthIndex}
                                        onClick={() => handleMonthSelect(month.monthIndex)}
                                        className={`flex-shrink-0 px-3 py-2 rounded-xs border text-xs font-medium transition-all ${
                                            isSelected
                                                ? "bg-black text-white border-black"
                                                : "bg-white text-gray-700 border-gray-200 hover:bg-[#F9F6F3] hover:border-gray-300"
                                        }`}
                                    >
                                        <div className="flex flex-col items-center gap-1">
                                            <span className="font-semibold whitespace-nowrap">
                                                {new Date(month.startDate).toLocaleDateString("en-US", { month: "short", year: "numeric" })}
                                            </span>
                                            {month.total > 0 ? (
                                                <>
                                                    <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${
                                                        isSelected ? getPercentageColor(month.handledPercentage) : getPercentageColorUnselected(month.handledPercentage)
                                                    }`}>
                                                        ✓ {month.handled}/{month.total} ({month.handledPercentage}%)
                                                    </span>
                                                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                                                        isSelected ? getPercentageColor(month.linkedinApprovedPercentage) : getPercentageColorUnselected(month.linkedinApprovedPercentage)
                                                    }`}>
                                                        in {month.linkedinApprovedPercentage}%
                                                    </span>
                                                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                                                        isSelected ? getPercentageColor(month.gmapsApprovedPercentage) : getPercentageColorUnselected(month.gmapsApprovedPercentage)
                                                    }`}>
                                                        gm {month.gmapsApprovedPercentage}%
                                                    </span>
                                                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                                                        isSelected ? getPercentageColor(month.emailApprovedPercentage) : getPercentageColorUnselected(month.emailApprovedPercentage)
                                                    }`}>
                                                        @ {month.emailApprovedPercentage}%
                                                    </span>
                                                </>
                                            ) : (
                                                <span className="text-[10px] text-gray-400 px-1.5 py-0.5">No events</span>
                                            )}
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </div>

                {/* Search and Filters */}
                {(() => {
                    const activeFilterCount = [
                        filters.signalCreated,
                        filters.enrichmentStatus,
                        filters.hasNotes,
                        filters.eventType,
                        filters.minScore,
                        filters.maxScore,
                        filters.startDate,
                        filters.endDate,
                        filters.enrichmentFilters?.length ? "yes" : undefined,
                    ].filter(v => v !== undefined).length;

                    return (
                        <div className="bg-white border border-gray-200 rounded-xs p-4 mb-6">
                            {/* Top row — always visible */}
                            <div className="flex flex-wrap gap-2 items-center">
                                {/* City */}
                                <div className="flex items-center gap-1.5 px-2.5 py-1.5 border border-gray-200 rounded-xs bg-white">
                                    <MapPin className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                                    <input
                                        type="text"
                                        placeholder="City..."
                                        value={filters.city || ""}
                                        onChange={(e) => handleFilterChange("city", e.target.value)}
                                        className="border-0 focus:ring-0 focus:outline-none text-xs placeholder:text-gray-400 w-24"
                                    />
                                </div>

                                {/* Search */}
                                <div className="flex items-center gap-1.5 px-2.5 py-1.5 border border-gray-200 rounded-xs bg-white flex-1 min-w-[180px]">
                                    <Search className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                                    <input
                                        type="text"
                                        placeholder="Search by company, source, type..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                                        className="flex-1 border-0 focus:ring-0 focus:outline-none text-xs placeholder:text-gray-400"
                                    />
                                </div>

                                {/* Sort field */}
                                <select
                                    value={filters.sortBy || "detectedAt"}
                                    onChange={(e) => handleFilterChange("sortBy", e.target.value)}
                                    className="px-2.5 py-1.5 border border-gray-200 rounded-xs bg-white text-xs text-gray-700 focus:ring-1 focus:ring-gray-900 focus:outline-none"
                                >
                                    <option value="detectedAt">Date detected</option>
                                    <option value="score.total">Score</option>
                                    <option value="createdAt">Date created</option>
                                    <option value="companyName">Company A→Z</option>
                                </select>

                                {/* Sort order */}
                                <button
                                    onClick={() => handleFilterChange("sortOrder", filters.sortOrder === "asc" ? "desc" : "asc")}
                                    className="px-2.5 py-1.5 border border-gray-200 rounded-xs bg-white text-xs text-gray-700 hover:bg-[#F9F6F3] transition-colors"
                                    title="Toggle sort order"
                                >
                                    {filters.sortOrder === "asc" ? "↑ Asc" : "↓ Desc"}
                                </button>

                                {/* Filters toggle */}
                                <button
                                    onClick={() => setShowFilters(!showFilters)}
                                    className={`flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded-xs border transition-colors ${
                                        showFilters || activeFilterCount > 0
                                            ? "bg-black text-white border-black"
                                            : "bg-white text-gray-700 border-gray-200 hover:bg-[#F9F6F3]"
                                    }`}
                                >
                                    <Filter className="w-3.5 h-3.5" />
                                    Filters
                                    {activeFilterCount > 0 && (
                                        <span className="bg-white text-black rounded-full w-4 h-4 text-[10px] font-bold flex items-center justify-center">
                                            {activeFilterCount}
                                        </span>
                                    )}
                                </button>

                                {/* Refresh */}
                                <button
                                    onClick={() => { setPage(1); fetchData(); }}
                                    className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded-xs border bg-white text-gray-700 border-gray-200 hover:bg-[#F9F6F3] transition-colors"
                                    title="Refresh"
                                >
                                    <RefreshCw className="w-3.5 h-3.5" />
                                </button>
                            </div>

                            {/* Collapsible filter panel */}
                            {showFilters && (
                                <div className="mt-4 pt-4 border-t border-gray-200">
                                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-8 gap-3">
                                        {/* Date from */}
                                        <div>
                                            <label className="block text-xs font-medium text-gray-700 mb-1">From</label>
                                            <input
                                                type="date"
                                                value={filters.startDate ? filters.startDate.substring(0, 10) : ""}
                                                onChange={(e) => {
                                                    setSelectedMonth(null);
                                                    handleFilterChange("startDate", e.target.value);
                                                }}
                                                className="w-full px-2.5 py-1.5 border border-gray-200 rounded-xs bg-white text-gray-900 text-xs focus:ring-1 focus:ring-gray-900 focus:outline-none"
                                            />
                                        </div>

                                        {/* Date to */}
                                        <div>
                                            <label className="block text-xs font-medium text-gray-700 mb-1">To</label>
                                            <input
                                                type="date"
                                                value={filters.endDate ? filters.endDate.substring(0, 10) : ""}
                                                onChange={(e) => {
                                                    setSelectedMonth(null);
                                                    handleFilterChange("endDate", e.target.value);
                                                }}
                                                className="w-full px-2.5 py-1.5 border border-gray-200 rounded-xs bg-white text-gray-900 text-xs focus:ring-1 focus:ring-gray-900 focus:outline-none"
                                            />
                                        </div>

                                        {/* Event Type */}
                                        <div>
                                            <label className="block text-xs font-medium text-gray-700 mb-1">
                                                Event Type
                                                {filters.eventType && (
                                                    <span className="ml-1 text-[10px] text-gray-400">({filters.eventType.split(',').length} selected)</span>
                                                )}
                                            </label>
                                            <select
                                                multiple
                                                value={filters.eventType?.split(',') || []}
                                                onChange={(e) => {
                                                    const selected = Array.from(e.target.selectedOptions, opt => opt.value);
                                                    handleFilterChange("eventType", selected.length > 0 ? selected.join(',') : undefined);
                                                }}
                                                className="w-full px-2.5 py-1.5 border border-gray-200 rounded-xs bg-white text-gray-900 text-xs focus:ring-1 focus:ring-gray-900 focus:outline-none"
                                                size={3}
                                            >
                                                <option value="new-restaurant">Restaurant Opening</option>
                                                <option value="hiring">Hiring</option>
                                                <option value="other">Other</option>
                                            </select>
                                        </div>

                                        {/* Signal Created */}
                                        <div>
                                            <label className="block text-xs font-medium text-gray-700 mb-1">Signal</label>
                                            <select
                                                value={filters.signalCreated || ""}
                                                onChange={(e) => handleFilterChange("signalCreated", e.target.value)}
                                                className="w-full px-2.5 py-1.5 border border-gray-200 rounded-xs bg-white text-gray-900 text-xs focus:ring-1 focus:ring-gray-900 focus:outline-none"
                                            >
                                                <option value="">All</option>
                                                <option value="true">Created ✅</option>
                                                <option value="false">Pending ⏳</option>
                                            </select>
                                        </div>

                                        {/* Enrichment Status */}
                                        <div>
                                            <label className="block text-xs font-medium text-gray-700 mb-1">Enrichments</label>
                                            <select
                                                value={filters.enrichmentStatus || ""}
                                                onChange={(e) => handleFilterChange("enrichmentStatus", e.target.value)}
                                                className="w-full px-2.5 py-1.5 border border-gray-200 rounded-xs bg-white text-gray-900 text-xs focus:ring-1 focus:ring-gray-900 focus:outline-none"
                                            >
                                                <option value="">All</option>
                                                <option value="approved">Approved ✓</option>
                                                <option value="needs_review">Needs review ?</option>
                                                <option value="none">Not started</option>
                                            </select>
                                        </div>

                                        {/* Enrichment type + status (multi-pair AND) */}
                                        <div className="col-span-2">
                                            <label className="block text-xs font-medium text-gray-700 mb-1">
                                                Enrichment type
                                                {(filters.enrichmentFilters?.length ?? 0) > 0 && (
                                                    <span className="ml-1 text-[10px] text-gray-400">({filters.enrichmentFilters!.length} AND)</span>
                                                )}
                                            </label>
                                            {/* Active pairs */}
                                            {(filters.enrichmentFilters?.length ?? 0) > 0 && (
                                                <div className="flex flex-wrap gap-1 mb-1">
                                                    {filters.enrichmentFilters!.map((f, i) => (
                                                        <span key={i} className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-gray-100 border border-gray-300 rounded text-[10px] text-gray-700">
                                                            {f.type}{f.status ? ` [${f.status}]` : ""}
                                                            <button onClick={() => removeEnrichmentFilter(i)} className="text-gray-400 hover:text-gray-700 leading-none">×</button>
                                                        </span>
                                                    ))}
                                                </div>
                                            )}
                                            {/* Add new pair */}
                                            <div className="flex gap-1">
                                                <select
                                                    value={pendingEnrichmentType}
                                                    onChange={(e) => setPendingEnrichmentType(e.target.value)}
                                                    className="flex-1 min-w-0 px-2 py-1.5 border border-gray-200 rounded-xs bg-white text-gray-900 text-xs focus:ring-1 focus:ring-gray-900 focus:outline-none"
                                                >
                                                    <option value="">Type…</option>
                                                    <option value="google_place">google_place</option>
                                                    <option value="google_place_DFS">google_place_DFS</option>
                                                    <option value="siren">siren</option>
                                                    <option value="restaurant_name">restaurant_name</option>
                                                    <option value="address">address</option>
                                                    <option value="website">website</option>
                                                    <option value="website_content_OTS">website_content_OTS</option>
                                                    <option value="linkedin">linkedin</option>
                                                    <option value="email_personal">email_personal</option>
                                                    <option value="email_company">email_company</option>
                                                    <option value="image">image</option>
                                                    <option value="company_linked">company_linked</option>
                                                    <option value="franchise">franchise</option>
                                                </select>
                                                <select
                                                    value={pendingEnrichmentStatus}
                                                    onChange={(e) => setPendingEnrichmentStatus(e.target.value)}
                                                    className="flex-1 min-w-0 px-2 py-1.5 border border-gray-200 rounded-xs bg-white text-gray-900 text-xs focus:ring-1 focus:ring-gray-900 focus:outline-none"
                                                >
                                                    <option value="">Any status</option>
                                                    <option value="pending">pending</option>
                                                    <option value="processing">processing</option>
                                                    <option value="waiting_callback">waiting_callback</option>
                                                    <option value="done">done</option>
                                                    <option value="approved">approved</option>
                                                    <option value="error">error</option>
                                                </select>
                                                <button
                                                    onClick={addEnrichmentFilter}
                                                    disabled={!pendingEnrichmentType}
                                                    className="px-2.5 py-1.5 bg-black text-white text-xs rounded-xs disabled:opacity-40 hover:bg-gray-800 transition-colors flex-shrink-0"
                                                >
                                                    +
                                                </button>
                                            </div>
                                        </div>

                                        {/* Has Notes */}
                                        <div>
                                            <label className="block text-xs font-medium text-gray-700 mb-1">Notes</label>
                                            <select
                                                value={filters.hasNotes || ""}
                                                onChange={(e) => handleFilterChange("hasNotes", e.target.value)}
                                                className="w-full px-2.5 py-1.5 border border-gray-200 rounded-xs bg-white text-gray-900 text-xs focus:ring-1 focus:ring-gray-900 focus:outline-none"
                                            >
                                                <option value="">All</option>
                                                <option value="true">With notes</option>
                                                <option value="false">Without notes</option>
                                            </select>
                                        </div>

                                        {/* Score range */}
                                        <div>
                                            <label className="block text-xs font-medium text-gray-700 mb-1">Score</label>
                                            <div className="flex gap-1.5">
                                                <input
                                                    type="number"
                                                    placeholder="Min"
                                                    value={filters.minScore || ""}
                                                    onChange={(e) => handleFilterChange("minScore", e.target.value)}
                                                    className="w-1/2 px-2 py-1.5 border border-gray-200 rounded-xs bg-white text-gray-900 text-xs focus:ring-1 focus:ring-gray-900 focus:outline-none"
                                                    min="0" max="100"
                                                />
                                                <input
                                                    type="number"
                                                    placeholder="Max"
                                                    value={filters.maxScore || ""}
                                                    onChange={(e) => handleFilterChange("maxScore", e.target.value)}
                                                    className="w-1/2 px-2 py-1.5 border border-gray-200 rounded-xs bg-white text-gray-900 text-xs focus:ring-1 focus:ring-gray-900 focus:outline-none"
                                                    min="0" max="100"
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {activeFilterCount > 0 && (
                                        <div className="mt-3 flex justify-end">
                                            <button
                                                onClick={() => { setFilters({}); setSelectedMonth(null); }}
                                                className="text-xs text-gray-500 hover:text-gray-800 transition-colors"
                                            >
                                                Clear all filters
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })()}

                {/* Events Table */}
                {loading && page === 1 ? (
                    <div className="flex items-center justify-center h-64">
                        <div className="text-gray-500 text-sm">Loading events...</div>
                    </div>
                ) : error ? (
                    <div className="flex items-center justify-center h-64">
                        <div className="text-red-600 text-sm">{error}</div>
                    </div>
                ) : events.length === 0 ? (
                    <div className="text-center py-16">
                        <BarChart2 className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                        <h3 className="text-base font-semibold text-navy-700 dark:text-white mb-2">
                            No events found
                        </h3>
                        <p className="text-sm text-gray-500 mb-6">
                            {searchTerm ? "Try adjusting your search or filters" : "No events detected yet"}
                        </p>
                    </div>
                ) : (
                    <>
                        <div className="bg-white border border-gray-200 rounded-xs overflow-hidden">
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-4 py-3 text-left text-[11px] font-semibold text-gray-600 uppercase tracking-wider whitespace-nowrap">
                                                Signal
                                            </th>
                                            <th className="px-4 py-3 text-left text-[11px] font-semibold text-gray-600 uppercase tracking-wider whitespace-nowrap">
                                                Company
                                            </th>
                                            <th className="px-4 py-3 text-left text-[11px] font-semibold text-gray-600 uppercase tracking-wider whitespace-nowrap">
                                                Enrichments
                                            </th>
                                            <th className="px-4 py-3 text-left text-[11px] font-semibold text-gray-600 uppercase tracking-wider whitespace-nowrap">
                                                People
                                            </th>
                                            <th className="px-4 py-3 text-left text-[11px] font-semibold text-gray-600 uppercase tracking-wider whitespace-nowrap">
                                                Internal Notes
                                            </th>
                                            <th className="px-4 py-3 text-left text-[11px] font-semibold text-gray-600 uppercase tracking-wider whitespace-nowrap">
                                                Detected
                                            </th>
                                            <th className="px-4 py-3 text-right text-[11px] font-semibold text-gray-600 uppercase tracking-wider whitespace-nowrap">
                                                Actions
                                            </th>
                                        </tr>
                                    </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {events.map((event) => (
                                        <tr key={event._id} className="hover:bg-[#F9F6F3] transition-colors">
                                            <td className="px-4 py-3 whitespace-nowrap text-center">
                                                <span className="text-lg" title={event.signalCreated ? "Signal created" : "No signal yet"}>
                                                    {event.signalCreated ? "✅" : "⏳"}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3">
                                                <div className="flex flex-col max-w-xs">
                                                    <span className="text-xs max-w-[150px] font-semibold text-navy-700 dark:text-white truncate">
                                                        {event.companyName}
                                                    </span>
                                                    {event.enrichments?.find((e: any) => e.type === "restaurant_name" && e.status === "done")?.data?.restaurant_name && (
                                                        <span className="text-[10px] text-gray-500 truncate max-w-[150px]" title={event.enrichments.find((e: any) => e.type === "restaurant_name")?.data?.restaurant_name}>
                                                            {event.enrichments.find((e: any) => e.type === "restaurant_name")?.data?.restaurant_name}
                                                        </span>
                                                    )}
                                                    {event.eventType && (
                                                        <span className="text-[10px] text-gray-500 mt-0.5">
                                                            {event.eventType}
                                                        </span>
                                                    )}
                                                    {event.isManuallyVerified && (
                                                        <span className="text-[10px] text-green-600 mt-0.5 flex items-center gap-0.5">
                                                            <CheckCircle className="w-3 h-3" />
                                                            Verified
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                             <td className="px-4 py-3">
                                                {(() => {
                                                    const statusStyle: Record<string, { color: string; icon: string }> = {
                                                        approved:   { color: "bg-green-50 text-green-700 border-green-200",   icon: "✓" },
                                                        done:       { color: "bg-gray-100 text-gray-700 border-gray-200",       icon: "?" },
                                                        retry:      { color: "bg-orange-50 text-orange-700 border-orange-200", icon: "🔄" },
                                                        error:      { color: "bg-red-50 text-red-700 border-red-200",         icon: "✗" },
                                                        rejected:   { color: "bg-red-50 text-red-700 border-red-200",         icon: "✗" },
                                                        pending:    { color: "bg-yellow-50 text-yellow-700 border-yellow-200", icon: "⌛" },
                                                        processing: { color: "bg-yellow-50 text-yellow-700 border-yellow-200", icon: "⌛" },
                                                    };
                                                    const autoApprovedTypes = ["siren", "restaurant_name"];
                                                    const orderedTypes: { type: string; label: string; source: "event" | "people" }[] = [
                                                        { type: "siren",           label: "SIREN",   source: "event" },
                                                        { type: "restaurant_name", label: "Name",    source: "event" },
                                                        { type: "google_place",    label: "Maps",    source: "event" },
                                                        { type: "google_place_DFS", label: "Maps DFS", source: "event" },
                                                        { type: "company_email",   label: "Email",   source: "event" },
                                                        { type: "linkedin",        label: "LinkedIn",source: "people" },
                                                    ];
                                                    return (
                                                        <div className="flex flex-wrap gap-1 items-center">
                                                            {orderedTypes.map(({ type, label, source }) => {
                                                                let status: string | null = null;
                                                                if (source === "event") {
                                                                    const enr = event.enrichments?.find((e: any) => e.type === type);
                                                                    if (!enr) return null;
                                                                    status = autoApprovedTypes.includes(type) && enr.status === "done" ? "approved" : enr.status;
                                                                } else {
                                                                    const enrs = event.people?.flatMap((p: any) => p.enrichments?.filter((e: any) => e.type === type) ?? []) ?? [];
                                                                    if (enrs.length === 0) return null;
                                                                    const priority = ["approved", "done", "retry", "processing", "pending", "error", "rejected"];
                                                                    status = priority.find(s => enrs.some((e: any) => e.status === s)) ?? enrs[0].status;
                                                                }
                                                                const { color: colorClass, icon } = statusStyle[status!] ?? { color: "bg-gray-50 text-gray-500 border-gray-200", icon: "·" };
                                                                return (
                                                                    <span
                                                                        key={type}
                                                                        className={`px-1.5 py-0.5 text-[10px] font-semibold rounded-sm border ${colorClass}`}
                                                                        title={`${type}: ${status}`}
                                                                    >
                                                                        {icon} {label}
                                                                    </span>
                                                                );
                                                            })}
                                                        </div>
                                                    );
                                                })()}
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap">
                                                <div className="flex items-center gap-1 text-xs text-gray-600">
                                                    <Users className="w-4 h-4" />
                                                    {event.people?.length || 0}
                                                </div>
                                            </td>
                                            <td className="px-4 py-3">
                                                {editingEventId === event._id ? (
                                                    <div className="flex items-center gap-2">
                                                        <input
                                                            type="text"
                                                            value={editingNotes}
                                                            onChange={(e) => setEditingNotes(e.target.value)}
                                                            placeholder="Add internal notes..."
                                                            className="px-2 py-1 text-xs border border-gray-300 rounded-xs focus:ring-1 focus:ring-gray-900 focus:outline-none w-64"
                                                        />
                                                        <button
                                                            onClick={() => handleSaveNotes(event._id)}
                                                            className="text-green-600 hover:text-green-800 text-xs font-medium whitespace-nowrap"
                                                        >
                                                            Save
                                                        </button>
                                                        <button
                                                            onClick={handleCancelEditing}
                                                            className="text-gray-600 hover:text-gray-800 text-xs font-medium whitespace-nowrap"
                                                        >
                                                            Cancel
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <div
                                                        onClick={() => handleStartEditing(event)}
                                                        className="cursor-pointer hover:bg-[#F9F6F3] px-2 py-1 rounded-xs transition-colors"
                                                    >
                                                        {event.notes ? (
                                                            <span className="text-xs text-gray-700 line-clamp-2 max-w-[250px]">
                                                                {event.notes}
                                                            </span>
                                                        ) : (
                                                            <span className="text-xs text-gray-400 italic">Add internal notes...</span>
                                                        )}
                                                    </div>
                                                )}
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-500">
                                                {formatDate(event.detectedAt)}
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap text-right">
                                                <div className="flex items-center justify-end gap-2">
                                                    <button
                                                        onClick={() => handleCalculateScore(event._id)}
                                                        className="text-purple-600 hover:text-purple-800 transition-colors"
                                                        title="Calculate Score"
                                                    >
                                                        <BarChart2 className="w-4 h-4" />
                                                    </button>
                                                    <button
                                                        onClick={() => handleCreateCompanyAndSignals(event._id)}
                                                        className="text-green-600 hover:text-green-800 transition-colors"
                                                        title="Create Company & Signals"
                                                    >
                                                        <Building className="w-4 h-4" />
                                                    </button>
                                                    <button
                                                        onClick={() => handleViewDetails(event)}
                                                        className="text-gray-900 hover:underline transition-colors"
                                                        title="View Details"
                                                    >
                                                        <Eye className="w-4 h-4" />
                                                    </button>
                                                    <button
                                                        onClick={() => handleDelete(event._id)}
                                                        className="text-red-600 hover:text-red-800 transition-colors"
                                                        title="Delete"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            </div>
                        </div>

                        {/* Load More */}
                        {hasMore && (
                            <div className="flex justify-center mt-6">
                                <button
                                    onClick={loadMore}
                                    disabled={loading}
                                    className="px-4 py-2 bg-black hover:bg-gray-800 disabled:bg-gray-400 text-white text-xs rounded-xs transition-colors"
                                >
                                    {loading ? "Loading..." : "Load More"}
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* Event Detail Modal */}
            {showDetailModal && selectedEvent && (
                <EventDetailModal
                    event={selectedEvent}
                    onClose={() => {
                        setShowDetailModal(false);
                        setSelectedEvent(null);
                    }}
                    onTriggerEnrichment={handleTriggerEnrichment}
                    onRefresh={handleRefreshEvent}
                />
            )}
        </div>
    );
}
